import { Injectable, NgZone, inject } from '@angular/core';
import {
  BehaviorSubject,
  firstValueFrom,
  interval,
  Observable,
  of,
  shareReplay,
  Subscription,
  finalize,
  switchMap,
  timer,
  timeout,
} from 'rxjs';
import { HttpClient, HttpContext, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { catchError, distinctUntilChanged, map, tap } from 'rxjs/operators';

import { pickDashboardFor, AppRole } from '../../../auth/shared/role.utils';
import { environment } from '../../../../environments/environment';
import { SKIP_AUTH_REFRESH } from '../../interceptors/auth-expired.interceptor';
import { SpinnerService } from '../../../layout/spinner/service/spinner-service';

export type { AppRole } from '../../../auth/shared/role.utils';

export interface CurrentUser {
  userId: string;
  roles: AppRole[];
  email?: string;
}

export interface MeResponse {
  id: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  roles: string[];
  authenticated: boolean;
  isActive?: boolean;
  mfaEnabled?: boolean;
  accountType?: string;
}

export interface PreAuthResponse {
  message: string;
  userId: string;
  email?: string;
  mfaEnabled: boolean;
  validated: boolean;
  authenticatorConfigured: boolean;
  preAuthToken?: string;
  challengeId?: string;
  channels?: string[];
}

export interface MfaInitiateResponse {
  sent: boolean;
  channel: string;
  challengeId?: string;
  userId?: string | null;
  email?: string | null;
}

interface MfaInitiateApiResponse extends Partial<MfaInitiateResponse> {
  statusCode?: number;
  message?: string;
}

export interface MfaDeliveryCapabilities {
  smsAvailable: boolean;
  smsDestination: string | null;
}

export interface VerifyResponseShape {
  message: string;
  statusCode: number;
  user?: {
    loginAfterMfa?: boolean;
    roles?: AppRole[];
    userId?: string;
    accountType?: string;
    resetToken?: string;
    emailMasked?: string;
  };
}

export interface ForgotPasswordResponse {
  message: string;
  statusCode: number;
  user?: {
    id?: string;
    userId?: string;
    emailMasked?: string;
    authenticatorConfigured?: boolean;
  };
}

export interface AuthenticatorSetupResponse {
  manualEntryKey: string;
  qrCodeImageBase64: string;
}

export interface EnsureSessionOptions {
  allowRefresh?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly spinner = inject(SpinnerService);
  private readonly zone = inject(NgZone);

  private readonly base = environment.apiGatewayUrl;
  private readonly authBase = this.authApiBase();
  private readonly touchIntervalMs = 3 * 60 * 1000;
  private readonly accessTokenRefreshIntervalMs = 12 * 60 * 1000;
  private readonly cookieSettleDelayMs = 750;
  private readonly cachedSessionMaxAgeMs = 5 * 60 * 1000;
  private readonly transientVerifyGraceMs = 15 * 60 * 1000;
  private readonly authPresenceStorageKey = 'apex.public.authPresence.v1';

  private readonly currentUserSubject = new BehaviorSubject<CurrentUser | null>(null);
  readonly currentUser$ = this.currentUserSubject.asObservable();

  private readonly sessionSubject = new BehaviorSubject<MeResponse | null>(null);
  readonly session$Obs = this.sessionSubject.asObservable();

  readonly roles$ = this.session$Obs.pipe(
    map((session) => (session?.authenticated ? (session.roles ?? []) : [])),
    distinctUntilChanged((a, b) => a.join('|') === b.join('|')),
  );

  private loaded = false;
  private verify$: Observable<MeResponse | null> | null = null;
  private verifyAllowsRefresh: boolean | null = null;
  private refreshInFlight$: Observable<unknown> | null = null;
  private heartbeatSub?: Subscription;
  private refreshSub?: Subscription;
  private lastSessionVerifiedAt = 0;

  async preAuthenticate(dto: {
    email: string;
    password: string;
    rememberMe?: boolean;
    workspaceOrgId?: string | null;
    workspacePortalSlug?: string | null;
  }): Promise<PreAuthResponse> {
    let headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });
    if (dto.workspacePortalSlug) {
      headers = headers.set('X-Workspace-Portal-Slug', dto.workspacePortalSlug);
    }
    if (dto.workspaceOrgId) {
      headers = headers.set('X-Workspace-Org-Id', dto.workspaceOrgId);
    }

    return firstValueFrom(
      this.http.post<PreAuthResponse>(`${this.base}/api/v1/user/pre-authenticate`, dto, {
        headers,
        withCredentials: true,
      }),
    );
  }

  async startPasswordReset(email: string): Promise<ForgotPasswordResponse> {
    return firstValueFrom(
      this.http.post<ForgotPasswordResponse>(
        `${this.base}/api/v1/user/forgot-password`,
        { email },
        { withCredentials: true },
      ),
    );
  }

  async startInvitationAccountSetup(email: string): Promise<ForgotPasswordResponse> {
    return firstValueFrom(
      this.http.post<ForgotPasswordResponse>(
        `${this.base}/api/v1/user/invitation-account-setup`,
        { email },
        { withCredentials: true },
      ),
    );
  }

  async startInvitationAccountSetupForUrl(invitationUrl: string): Promise<ForgotPasswordResponse> {
    return firstValueFrom(
      this.http.post<ForgotPasswordResponse>(
        `${this.base}/api/v1/user/invitation-account-setup`,
        { invitationUrl },
        { withCredentials: true },
      ),
    );
  }

  async resetPassword(dto: { password: string; confirmPassword: string; token?: string }): Promise<{
    message: string;
    statusCode: number;
  }> {
    const body: Record<string, string> = {
      password: dto.password,
      confirmPassword: dto.confirmPassword,
    };

    if (dto.token) {
      body['token'] = dto.token;
    }

    return firstValueFrom(
      this.http.put<{ message: string; statusCode: number }>(
        `${this.base}/api/v1/user/reset-password`,
        body,
        { withCredentials: true },
      ),
    );
  }

  async logout(): Promise<void> {
    try {
      await firstValueFrom(
        this.http.post(`${this.base}/api/v1/user/logout`, {}, {
          responseType: 'text',
          withCredentials: true,
        }),
      );
    } finally {
      this.clearAuthState();
    }
  }

  async login(dto: {
    email: string;
    password: string;
    rememberMe?: boolean;
    workspaceOrgId?: string | null;
    workspacePortalSlug?: string | null;
  }): Promise<CurrentUser> {
    this.verify$ = null;
    this.loaded = false;

    const body = new URLSearchParams();
    body.set('username', dto.email);
    body.set('password', dto.password);
    body.set('grant_type', 'password');
    body.set('rememberMe', String(dto.rememberMe === true));
    if (dto.workspaceOrgId) {
      body.set('workspaceOrgId', dto.workspaceOrgId);
    }
    if (dto.workspacePortalSlug) {
      body.set('workspacePortalSlug', dto.workspacePortalSlug);
    }

    const raw = await firstValueFrom(
      this.http.post<any>(`${this.base}/api/v1/user/login`, body.toString(), {
        withCredentials: true,
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      }),
    );

    const user: CurrentUser = {
      userId: raw?.userId ?? raw?.id ?? '',
      roles: Array.isArray(raw?.roles)
        ? (raw.roles as AppRole[])
        : raw?.role
          ? [raw.role as AppRole]
          : [],
      email: raw?.email,
    };

    this.currentUserSubject.next(user);
    if (String(raw?.accountType ?? '').toUpperCase() !== 'TENANT') {
      this.startSessionHeartbeat();
    }
    return user;
  }

  refreshMe() {
    this.verify$ = null;
    return this.verifySession();
  }

  async settleAuthenticatedSession(): Promise<MeResponse | null> {
    const delaysMs = [this.cookieSettleDelayMs, 750, 1250, 2000];

    for (const delayMs of delaysMs) {
      await this.wait(delayMs);
      this.verify$ = null;
      this.loaded = false;

      const session = await this.ensureSession();
      if (session?.authenticated) {
        return session;
      }
    }

    return null;
  }

  verifySession(): Observable<MeResponse | null> {
    return this.verifySessionWithOptions();
  }

  verifySessionWithOptions(options: EnsureSessionOptions = {}): Observable<MeResponse | null> {
    const allowRefresh = options.allowRefresh !== false;
    if (this.verify$ && this.verifyAllowsRefresh === allowRefresh) {
      return this.verify$;
    }

    const url = `${this.base}/api/v1/user/auth-verify`;
    const authContext = new HttpContext().set(SKIP_AUTH_REFRESH, true);
    const verifyRequest = () => this.http.get<MeResponse>(url, {
      context: authContext,
      withCredentials: true,
    });

    this.verifyAllowsRefresh = allowRefresh;
    this.verify$ = verifyRequest().pipe(
      tap((session) => this.acceptVerifiedSession(session)),
      catchError((error: unknown) => {
        if (!isAuthExpired(error) || !allowRefresh) {
          return this.handleVerifyFailure(error);
        }

        return this.refreshSession().pipe(
          switchMap(() => timer(this.cookieSettleDelayMs)),
          switchMap(() => verifyRequest()),
          tap((session) => this.acceptVerifiedSession(session)),
          catchError((retryError: unknown) => this.handleVerifyFailure(retryError)),
        );
      }),
      shareReplay({ bufferSize: 1, refCount: false }),
    );

    return this.verify$;
  }

  async ensureLoaded(): Promise<void> {
    if (this.loaded && this.getSession() && this.isCachedSessionFresh()) {
      return;
    }

    try {
      if (this.loaded && this.getSession()) {
        this.verify$ = null;
      }
      await firstValueFrom(this.verifySessionWithOptions());
    } finally {
      this.loaded = true;
    }
  }

  async ensureSession(options: EnsureSessionOptions = {}): Promise<MeResponse | null> {
    const cached = this.getSession();
    if (cached?.authenticated && this.isCachedSessionFresh()) {
      return cached;
    }

    if (cached?.authenticated) {
      this.verify$ = null;
    }

    return firstValueFrom(this.verifySessionWithOptions(options));
  }

  getSession(): MeResponse | null {
    return this.sessionSubject.value;
  }

  getCurrentUserSync(): CurrentUser | null {
    return this.currentUserSubject.value;
  }

  hasAuthPresenceHint(): boolean {
    if (typeof window === 'undefined') {
      return false;
    }

    return window.localStorage.getItem(this.authPresenceStorageKey) === 'true';
  }

  hasAnyRole(allowed: AppRole[] = []): boolean {
    const session = this.sessionSubject.value;

    if (!session?.authenticated) {
      return false;
    }

    return !allowed.length || allowed.some((role) => session.roles?.includes(role));
  }

  redirectPathForRoles(roles: AppRole[] | null | undefined): string {
    return pickDashboardFor(roles);
  }

  maskEmail(email: string): string {
    const [user, domain] = email.split('@');

    if (!domain) {
      return email;
    }

    const maskedUser =
      user.length <= 2
        ? `${user[0] ?? '*'}*`
        : `${user[0]}${'*'.repeat(Math.max(1, user.length - 2))}${user[user.length - 1]}`;

    return `${maskedUser}@${domain}`;
  }

  async getMfaDeliveryCapabilities(dto: {
    userId: string;
    preAuthToken: string;
    purpose: string;
  }): Promise<MfaDeliveryCapabilities> {
    return firstValueFrom(
      this.http.post<MfaDeliveryCapabilities>(
        `${this.base}/api/v1/user/mfa/delivery-capabilities`,
        dto,
        {
          context: new HttpContext().set(SKIP_AUTH_REFRESH, true),
          withCredentials: true,
        },
      ).pipe(
        timeout(8000),
        map((response) => ({
          smsAvailable: response?.smsAvailable === true,
          smsDestination: response?.smsAvailable === true && typeof response.smsDestination === 'string'
            ? response.smsDestination
            : null,
        })),
        catchError(() => of({ smsAvailable: false, smsDestination: null })),
      ),
    );
  }

  async initiateMfa(dto: {
    purpose: string;
    channel: string;
    challengeId?: string;
    userId?: string;
    email?: string;
    preAuthToken?: string;
  }): Promise<MfaInitiateResponse> {
    const body: Record<string, unknown> = {
      purpose: dto.purpose,
      channel: dto.channel,
    };

    if (dto.challengeId) {
      body['challengeId'] = dto.challengeId;
    }
    if (dto.userId && (!dto.challengeId || dto.channel === 'SMS')) {
      body['userId'] = dto.userId;
    }

    if (dto.email) {
      body['email'] = dto.email;
    }

    if (dto.preAuthToken) {
      body['preAuthToken'] = dto.preAuthToken;
    }

    return firstValueFrom(
      this.http.post<MfaInitiateApiResponse>(`${this.base}/api/v1/user/send-token`, body, {
        withCredentials: true,
      }).pipe(
        map((response) => {
          const confirmed = response?.statusCode === 200
            || (response?.statusCode === undefined && response?.sent === true);
          if (!confirmed || response?.sent === false) {
            throw new Error(response?.message || 'mfa.codeDeliveryFailed');
          }

          return {
            sent: true,
            channel: response.channel ?? dto.channel,
            challengeId: response.challengeId,
            userId: response.userId,
            email: response.email,
          };
        }),
      ),
    );
  }

  async beginAuthenticatorSetup(preAuthToken: string): Promise<AuthenticatorSetupResponse> {
    return firstValueFrom(
      this.http.post<AuthenticatorSetupResponse>(
        `${this.base}/api/v1/user/mfa/authenticator/setup`,
        { preAuthToken },
        { withCredentials: true },
      ),
    );
  }

  async verifyMfa(dto: {
    otpCode: string;
    purpose: string;
    channel?: string;
    challengeId?: string;
    userId?: string;
    email?: string;
    workspaceOrgId?: string | null;
    workspacePortalSlug?: string | null;
    rememberMe?: boolean;
    preAuthToken?: string | null;
  }): Promise<VerifyResponseShape> {
    const body: Record<string, unknown> = {
      otpCode: dto.otpCode,
      purpose: dto.purpose,
    };

    if (dto.channel) {
      body['channel'] = dto.channel;
    }

    if (dto.challengeId) {
      body['challengeId'] = dto.challengeId;
    }
    if (dto.userId && (!dto.challengeId || dto.channel === 'SMS')) {
      body['userId'] = dto.userId;
    }

    if (dto.email) {
      body['email'] = dto.email;
    }
    if (dto.workspaceOrgId) {
      body['workspaceOrgId'] = dto.workspaceOrgId;
    }
    if (dto.workspacePortalSlug) {
      body['workspacePortalSlug'] = dto.workspacePortalSlug;
    }
    body['rememberMe'] = dto.rememberMe === true;
    // Session-issuing purposes must prove the password step that started this challenge.
    if (dto.preAuthToken && (dto.purpose === 'LOGIN' || dto.purpose === 'SIGNUP')) {
      body['preAuthToken'] = dto.preAuthToken;
    }

    return firstValueFrom(
      this.http.post<VerifyResponseShape>(`${this.base}/api/v1/user/verify`, body, {
        withCredentials: true,
      }),
    );
  }

  startSessionHeartbeat(): void {
    if (this.heartbeatSub) {
      return;
    }

    this.zone.runOutsideAngular(() => {
      this.touchSession();
      this.heartbeatSub = interval(this.touchIntervalMs).subscribe(() => {
        this.touchSession();
      });
    });
    this.startAccessRefreshHeartbeat();
  }

  private touchSession(): void {
    this.http
      .post(`${this.base}/api/v1/sessions/touch`, {}, {
        context: new HttpContext().set(SKIP_AUTH_REFRESH, true),
        withCredentials: true,
      })
      .subscribe({
        next: () => undefined,
        error: (err) => console.warn('[Heartbeat] touch failed', err?.message),
      });
  }

  private authApiBase(): string {
    const trimmed = this.base.replace(/\/+$/, '');
    return trimmed.endsWith('/dev') ? `${trimmed}/auth` : trimmed;
  }

  backofficeDashboardUrl(workspace?: { orgId?: string | null; portalSlug?: string | null; landingPath?: string | null }): string {
    const url = new URL(`${this.backofficeBaseUrl(workspace?.portalSlug)}${this.safeLandingPath(workspace?.landingPath)}`);

    if (workspace?.orgId) {
      url.searchParams.set('orgId', workspace.orgId);
    }

    return url.toString();
  }

  tenantDashboardUrl(workspace?: { orgId?: string | null; portalSlug?: string | null; landingPath?: string | null }): string {
    const url = new URL(`${this.portalBaseUrl(environment.tenantAppUrl, workspace?.portalSlug)}${this.safeLandingPath(workspace?.landingPath)}`);

    if (workspace?.orgId) {
      url.searchParams.set('orgId', workspace.orgId);
    }

    return url.toString();
  }

  ownerDashboardUrl(workspace?: { orgId?: string | null; portalSlug?: string | null; landingPath?: string | null }): string {
    const landingPath = workspace?.landingPath || '/dashboard';
    const url = new URL(`${this.portalBaseUrl(environment.ownerAppUrl, workspace?.portalSlug)}${this.safeLandingPath(landingPath)}`);

    if (workspace?.orgId) {
      url.searchParams.set('orgId', workspace.orgId);
    }

    return url.toString();
  }

  platformAdminDashboardUrl(landingPath = '/dashboard'): string {
    return new URL(`${environment.platformAdminAppUrl}${this.safeLandingPath(landingPath)}`).toString();
  }

  platformAdminLoginUrl(): string {
    return new URL('/login', environment.platformAdminAppUrl).toString();
  }

  maintenanceDashboardUrl(landingPath = '/dashboard', orgId?: string | null): string {
    const url = new URL(`${environment.maintenanceAppUrl}${this.safeLandingPath(landingPath)}`);
    if (orgId) url.searchParams.set('orgId', orgId);
    return url.toString();
  }

  stopSessionHeartbeat(): void {
    this.heartbeatSub?.unsubscribe();
    this.heartbeatSub = undefined;
    this.stopAccessRefreshHeartbeat();
  }

  clearAuthState(): void {
    this.verify$ = null;
    this.verifyAllowsRefresh = null;
    this.loaded = false;
    this.lastSessionVerifiedAt = 0;
    this.clearAuthPresenceHint();
    this.currentUserSubject.next(null);
    this.sessionSubject.next(null);
    this.stopSessionHeartbeat();
  }

  private acceptVerifiedSession(session: MeResponse | null): void {
    this.sessionSubject.next(session?.authenticated ? session : null);

    if (session?.authenticated) {
      this.lastSessionVerifiedAt = Date.now();
      this.markAuthPresenceHint();
      this.startSessionHeartbeat();
    } else {
      this.lastSessionVerifiedAt = 0;
      this.clearAuthPresenceHint();
      this.stopSessionHeartbeat();
    }
  }

  private handleVerifyFailure(error?: unknown): Observable<MeResponse | null> {
    this.verify$ = null;
    this.verifyAllowsRefresh = null;

    const current = this.sessionSubject.value;
    if (current?.authenticated && this.shouldPreserveSessionOnVerifyError(error)) {
      return of(current);
    }

    this.lastSessionVerifiedAt = 0;
    this.clearAuthPresenceHint();
    this.stopSessionHeartbeat();
    this.sessionSubject.next(null);
    return of(null);
  }

  private isCachedSessionFresh(): boolean {
    return this.lastSessionVerifiedAt > 0
      && Date.now() - this.lastSessionVerifiedAt < this.cachedSessionMaxAgeMs;
  }

  private shouldPreserveSessionOnVerifyError(error?: unknown): boolean {
    if (!this.isWithinTransientVerifyGrace()) {
      return false;
    }

    if (!(error instanceof HttpErrorResponse)) {
      return false;
    }

    return error.status === 0 || error.status === 408 || error.status === 429 || error.status >= 500;
  }

  private isWithinTransientVerifyGrace(): boolean {
    return this.lastSessionVerifiedAt > 0
      && Date.now() - this.lastSessionVerifiedAt < this.transientVerifyGraceMs;
  }

  private startAccessRefreshHeartbeat(): void {
    if (this.refreshSub || typeof window === 'undefined') {
      return;
    }

    this.zone.runOutsideAngular(() => {
      this.refreshSub = interval(this.accessTokenRefreshIntervalMs).subscribe(() => {
        this.refreshSession().subscribe({
          next: () => undefined,
          error: () => undefined,
        });
      });
    });
  }

  private stopAccessRefreshHeartbeat(): void {
    this.refreshSub?.unsubscribe();
    this.refreshSub = undefined;
  }

  private refreshSession(): Observable<unknown> {
    if (!this.refreshInFlight$) {
      this.refreshInFlight$ = this.http.post(`${this.base}/api/v1/user/refresh`, {}, {
        context: new HttpContext().set(SKIP_AUTH_REFRESH, true),
        responseType: 'text',
        withCredentials: true,
      }).pipe(
        timeout(8000),
        tap(() => {
          this.verify$ = null;
          this.verifyAllowsRefresh = null;
          this.loaded = false;
        }),
        finalize(() => {
          this.refreshInFlight$ = null;
        }),
        shareReplay({ bufferSize: 1, refCount: false }),
      );
    }

    return this.refreshInFlight$;
  }

  private wait(delayMs: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, delayMs));
  }

  private markAuthPresenceHint(): void {
    if (typeof window === 'undefined') {
      return;
    }

    window.localStorage.setItem(this.authPresenceStorageKey, 'true');
  }

  private clearAuthPresenceHint(): void {
    if (typeof window === 'undefined') {
      return;
    }

    window.localStorage.removeItem(this.authPresenceStorageKey);
  }

  private backofficeBaseUrl(portalSlug?: string | null): string {
    return this.portalBaseUrl(environment.backofficeAppUrl, portalSlug);
  }

  private safeLandingPath(path?: string | null): string {
    const normalized = path?.trim();
    return normalized?.startsWith('/') ? normalized : '/dashboard';
  }

  private portalBaseUrl(baseUrl: string, portalSlug?: string | null): string {
    if (!portalSlug?.trim()) {
      return baseUrl;
    }

    try {
      const url = new URL(baseUrl);
      const slug = portalSlug.trim().toLowerCase();

      if (url.hostname === 'localhost' || /^\d{1,3}(\.\d{1,3}){3}$/.test(url.hostname)) {
        return url.origin;
      }

      if (!url.hostname.startsWith(`${slug}.`)) {
        url.hostname = `${slug}.${url.hostname}`;
      }

      return url.origin;
    } catch {
      return baseUrl;
    }
  }
}

function isAuthExpired(error: unknown): boolean {
  if (!(error instanceof HttpErrorResponse)) {
    return false;
  }

  const errorCode = String(error.error?.error ?? error.error?.errorCode ?? '').toUpperCase();
  const message = String(error.error?.message ?? '').toLowerCase();
  const url = String(error.url ?? '').toLowerCase();
  const authEndpoint = url.includes('/api/v1/user/');

  return (
    error.status === 401 ||
    errorCode === 'AUTHORIZATION_FAILED' ||
    (error.status === 403 && authEndpoint && message.includes('authorization failed'))
  );
}
